import { useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { ErrorNote, formatDate, timeAgo, useApi } from "../components/ui";
import { useMe } from "../App";
import { useTour } from "../tour/Tour";

interface Token {
  id: string;
  prefix: string;
  name: string;
  scope: "read" | "write";
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
}

export function Account() {
  const { me, refresh } = useMe();
  const tour = useTour();
  const [discordId, setDiscordId] = useState(me.discordUserId ?? "");
  const [discordMsg, setDiscordMsg] = useState<string | null>(null);
  const saveDiscord = async (e: FormEvent) => {
    e.preventDefault();
    setDiscordMsg(null);
    try {
      await api("/auth/me", { method: "PATCH", json: { discordUserId: discordId.trim() || null } });
      setDiscordMsg(discordId.trim() ? "Linked." : "Unlinked.");
      refresh();
    } catch (err) {
      setDiscordMsg(err instanceof ApiError ? err.message : "Couldn't save.");
    }
  };
  const [name, setName] = useState(me.name);
  const [saved, setSaved] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    await api("/auth/me", { method: "PATCH", json: { name } });
    setSaved(true);
    refresh();
  };
  return (
    <div className="page-narrow stack">
      <h1>Your account</h1>
      <form className="card stack" onSubmit={save}>
        <dl className="meta">
          <dt>Email</dt>
          <dd>{me.email}</dd>
          <dt>Company</dt>
          <dd>{me.role === "staff" ? "Departure Labs (staff)" : me.orgName}</dd>
        </dl>
        <div className="field">
          <label htmlFor="name">Display name</label>
          <input id="name" type="text" value={name} maxLength={120} onChange={(e) => (setName(e.target.value), setSaved(false))} />
        </div>
        <div className="row">
          <button className="btn btn-primary">Save</button>
          {saved && <span className="small muted" role="status">Saved.</span>}
        </div>
      </form>
      {me.role === "staff" && (
        <form className="card stack" onSubmit={saveDiscord}>
          <div>
            <h2>Discord mentions</h2>
            <p className="small muted" style={{ margin: "6px 0 0" }}>
              Get @mentioned in the portal's Discord channels when something is assigned to you. In Discord: Settings → Advanced → turn on Developer Mode, then right-click
              your name → Copy User ID.
            </p>
          </div>
          <div className="row" style={{ alignItems: "flex-end" }}>
            <div className="field" style={{ flex: 1, minWidth: 200 }}>
              <label htmlFor="discord-id">Discord user ID</label>
              <input id="discord-id" type="text" inputMode="numeric" className="mono" placeholder="e.g. 123456789012345678" value={discordId} onChange={(e) => setDiscordId(e.target.value.replace(/\D/g, ""))} maxLength={20} />
            </div>
            <button className="btn">Save</button>
          </div>
          {discordMsg && <span className="small muted" role="status">{discordMsg}</span>}
        </form>
      )}
      <section className="card row" style={{ justifyContent: "space-between" }}>
        <div>
          <h2>Welcome tour</h2>
          <p className="small muted" style={{ margin: "6px 0 0" }}>A two-minute walk through the portal, plus short how-tos.</p>
        </div>
        <button className="btn" onClick={() => tour.start("main")} data-tour="replay-tour">
          Replay the welcome tour
        </button>
      </section>
      <ApiTokens />
    </div>
  );
}

function ApiTokens() {
  const { me } = useMe();
  const { data, error, reload } = useApi<{ tokens: Token[] }>("/tokens");
  const [name, setName] = useState("");
  const [scope, setScope] = useState<"read" | "write">("read");
  const [created, setCreated] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const create = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      const r = await api<{ token: string }>("/tokens", { method: "POST", json: { name, scope } });
      setCreated(r.token);
      setName("");
      reload();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Couldn't create the token.");
    }
  };
  const active = data?.tokens.filter((t) => !t.revoked_at) ?? [];
  return (
    <section className="card stack">
      <div>
        <h2>API tokens</h2>
        <p className="small muted" style={{ margin: "6px 0 0" }}>
          Use the portal's API from scripts and other tools. A token acts as you{me.role === "customer" ? ` and only sees ${me.orgName}'s data` : ""}. Send it as{" "}
          <code className="mono">Authorization: Bearer …</code> to <code className="mono">/api/v1</code>.
        </p>
      </div>
      <ErrorNote message={error} />
      {created && (
        <div className="notice stack" role="status">
          <strong>Copy this token now. It won't be shown again.</strong>
          <div className="keybox">{created}</div>
          <div className="row">
            <button className="btn btn-small" onClick={() => navigator.clipboard.writeText(created)}>Copy</button>
            <button className="btn btn-small" onClick={() => setCreated(null)}>Done</button>
          </div>
        </div>
      )}
      <form className="row" onSubmit={create} style={{ alignItems: "flex-end" }}>
        <div className="field" style={{ flex: 1, minWidth: 180 }}>
          <label htmlFor="tok-name">Name</label>
          <input id="tok-name" type="text" required maxLength={80} placeholder="e.g. Zapier, reporting script" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="tok-scope">Access</label>
          <select id="tok-scope" value={scope} onChange={(e) => setScope(e.target.value as "read" | "write")}>
            <option value="read">Read only</option>
            <option value="write">Read and write</option>
          </select>
        </div>
        <button className="btn">Create token</button>
      </form>
      {err && <p className="error-text" role="alert">{err}</p>}
      {active.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Access</th>
                <th>Last used</th>
                <th>Expires</th>
                <th><span className="sr-only">Revoke</span></th>
              </tr>
            </thead>
            <tbody>
              {active.map((t) => (
                <tr key={t.id}>
                  <td>
                    {t.name} <span className="mono small muted">dlt_{t.prefix}…</span>
                  </td>
                  <td>{t.scope === "write" ? "Read & write" : "Read only"}</td>
                  <td className="small">{t.last_used_at ? timeAgo(t.last_used_at) : "Never"}</td>
                  <td className="small">{t.expires_at ? formatDate(t.expires_at) : "Never"}</td>
                  <td>
                    <button className="btn btn-small btn-danger" onClick={() => api(`/tokens/${t.id}`, { method: "DELETE" }).then(reload)}>
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
